import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { PageContainer } from '@/components/layout/PageContainer';
import { EmptyState } from '@/components/tables/EmptyState';
import { ActionButton } from '@/components/ui/ActionButton';
import { apiClient } from '@/lib/api';
import { useRefresh } from '@/hooks/useRefresh';
import { getAutomation } from '@/automations';
import type { ApprovalRequest } from '@/lib/types';

export default function AutomationDetailPage() {
  const { slug = '', id = '' } = useParams<{ slug: string; id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refreshKey } = useRefresh();
  const config = getAutomation(slug);

  const { data, isLoading, error } = useQuery<ApprovalRequest>({
    queryKey: ['automation', slug, 'detail', id, refreshKey],
    queryFn: async () => {
      const res = await apiClient.get<ApprovalRequest>(`/automations/${slug}/${id}`);
      return res.data;
    },
    enabled: !!slug && !!id && !!config,
  });

  if (!config) {
    return (
      <PageContainer>
        <EmptyState title="Unknown automation" description={`No automation with slug "${slug}".`} />
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center gap-8 py-64 text-neutral-500">
          <Loader2 className="h-20 w-20 animate-spin" />
          <span>Loading request…</span>
        </div>
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer>
        <EmptyState
          title="Couldn't load request"
          description={(error as Error | undefined)?.message ?? 'Try the refresh button.'}
        />
      </PageContainer>
    );
  }

  const Detail = config.DetailComponent;

  return (
    <PageContainer>
      <div className="mb-12">
        <ActionButton
          variant="ghost"
          size="sm"
          leadingIcon={<ArrowLeft className="h-16 w-16" />}
          onClick={() => navigate(`/automations/${slug}`)}
        >
          Back to queue
        </ActionButton>
      </div>
      <Detail
        request={data}
        onAction={() => {
          void queryClient.invalidateQueries({ queryKey: ['automation', slug] });
        }}
      />
    </PageContainer>
  );
}
